import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, LockKeyhole } from "lucide-react";
import { API, waLink } from "@/lib/config";
import { Reveal, MaskLines, EASE } from "@/components/ui/motion";

const TYPES = ["Pinjaman Online", "Kartu Kredit", "KTA", "Lebih dari satu jenis"];
const RANGES = ["< Rp10 juta", "Rp10–50 juta", "Rp50–100 juta", "Rp100–250 juta", "> Rp250 juta", "Belum tahu"];
const EMPTY = { full_name: "", whatsapp: "", email: "", obligation_type: "", estimated_total: "", message: "", consent: false };

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Mohon isi nama lengkap Anda.";
  if (!/^(\+?62|0)8\d{7,12}$/.test(f.whatsapp.replace(/[\s-]/g, ""))) e.whatsapp = "Gunakan format nomor WhatsApp Indonesia, mis. 0812xxxxxxx.";
  if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Format email belum sesuai.";
  if (!f.obligation_type) e.obligation_type = "Pilih jenis kewajiban.";
  if (!f.estimated_total) e.estimated_total = "Pilih perkiraan total kewajiban.";
  if (!f.consent) e.consent = "Persetujuan pemrosesan data wajib dicentang.";
  return e;
};

const Field = ({ label, error, id, optional, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 flex items-center justify-between text-[13px] font-semibold text-slateb">
      {label}{optional && <span className="font-medium text-ink-soft">Opsional</span>}
    </span>
    {children}
    {error && <span className="mt-1.5 block text-[12.5px] font-medium text-red-600" data-testid={`error-${id}`}>{error}</span>}
  </label>
);

const STATIC_MODE = !process.env.REACT_APP_BACKEND_URL;

const waSummary = (d) =>
  `Halo Mika Solution, saya ${d.full_name} (${d.whatsapp}). Saya ingin berkonsultasi mengenai kewajiban ${d.obligation_type} dengan perkiraan total ${d.estimated_total}.${d.message ? ` Catatan: ${d.message}` : ""}`;

const Success = ({ data, onReset }) => {
  const msg = waSummary(data);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="py-6 text-center" data-testid="consult-success">
      <CheckCircle2 size={52} className="mx-auto text-accentb" />
      <h3 className="mt-6 font-display text-2xl font-bold sm:text-3xl">{STATIC_MODE ? "Pesan Anda siap dikirim." : "Data Anda telah kami terima."}</h3>
      <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-ink">{STATIC_MODE ? `Terima kasih, ${data.full_name}. WhatsApp telah dibuka dengan ringkasan kondisi Anda. Tekan kirim di WhatsApp agar tim kami menerimanya.` : `Terima kasih, ${data.full_name}. Tim Mika Solution akan menghubungi Anda melalui WhatsApp. Anda juga dapat langsung melanjutkan percakapan sekarang.`}</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a href={waLink("default", msg)} target="_blank" rel="noopener noreferrer" className="btn btn-sky" data-testid="consult-success-wa">Lanjutkan ke WhatsApp <ArrowUpRight size={16} /></a>
        <button type="button" onClick={onReset} className="btn btn-ghost" data-testid="consult-reset">Kirim formulir lain</button>
      </div>
    </motion.div>
  );
};

export const ConsultForm = ({ defaultType = "" }) => {
  const [f, setF] = useState({ ...EMPTY, obligation_type: defaultType });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length) return;
    if (STATIC_MODE) {
      window.open(waLink("default", waSummary(f)), "_blank", "noopener");
      setSent(f);
      setStatus("success");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/consultations`, { ...f, email: f.email || null, message: f.message || null });
      setSent(f);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };
  const reset = () => { setF({ ...EMPTY, obligation_type: defaultType }); setStatus("idle"); setSent(null); };

  return (
    <section id="konsultasi" className="relative overflow-hidden bg-ice py-20 sm:py-28 lg:py-36" data-testid="consult-section">
      <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_bottom_right,black,transparent_70%)]" aria-hidden="true" />
      <div className="wrap relative grid grid-cols-1 gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="eyebrow">Formulir Konsultasi</span>
          <MaskLines as="h2" inView className="mt-5 font-display text-4xl font-bold leading-[1.02] sm:text-5xl lg:text-[64px]" lines={["Mulai Langkah", <span className="serif-it font-normal text-accentb">Pertama Anda.</span>]} />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink md:text-lg">Ceritakan gambaran kondisi kewajiban finansial Anda. Tim Mika Solution akan membantu mengarahkan Anda ke tahap konsultasi berikutnya.</p>
            <p className="mt-8 flex max-w-md gap-3 rounded-2xl bg-white/70 p-5 text-[13.5px] leading-relaxed text-ink">
              <LockKeyhole size={18} className="mt-0.5 shrink-0 text-accentb" />
              Kami tidak pernah meminta PIN, CVV, OTP, password, atau kredensial perbankan Anda.
            </p>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7">
          <div className="card p-6 shadow-[0_40px_80px_-50px_rgba(51,78,104,.5)] sm:p-10">
            <AnimatePresence initial={false}>
              {status === "success" && sent ? (
                <Success key="ok" data={sent} onReset={reset} />
              ) : (
                <form key="form" onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="consult-form">
                  <Field label="Nama lengkap" id="full_name" error={errors.full_name}>
                    <input id="full_name" className="field" value={f.full_name} onChange={set("full_name")} aria-invalid={!!errors.full_name} autoComplete="name" data-testid="input-full-name" />
                  </Field>
                  <Field label="Nomor WhatsApp" id="whatsapp" error={errors.whatsapp}>
                    <input id="whatsapp" className="field" inputMode="tel" placeholder="0812xxxxxxxx" value={f.whatsapp} onChange={set("whatsapp")} aria-invalid={!!errors.whatsapp} autoComplete="tel" data-testid="input-whatsapp" />
                  </Field>
                  <Field label="Email" id="email" optional error={errors.email}>
                    <input id="email" type="email" className="field" value={f.email} onChange={set("email")} aria-invalid={!!errors.email} autoComplete="email" data-testid="input-email" />
                  </Field>
                  <Field label="Jenis kewajiban" id="obligation_type" error={errors.obligation_type}>
                    <select id="obligation_type" className="field" value={f.obligation_type} onChange={set("obligation_type")} aria-invalid={!!errors.obligation_type} data-testid="select-obligation-type">
                      <option value="">Pilih jenis</option>
                      {TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Perkiraan total kewajiban" id="estimated_total" error={errors.estimated_total}>
                      <select id="estimated_total" className="field" value={f.estimated_total} onChange={set("estimated_total")} aria-invalid={!!errors.estimated_total} data-testid="select-estimated-total">
                        <option value="">Pilih perkiraan</option>
                        {RANGES.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Pesan tambahan" id="message" optional>
                      <textarea id="message" rows={4} maxLength={1000} className="field resize-none" value={f.message} onChange={set("message")} placeholder="Ceritakan singkat kondisi Anda (tanpa data rahasia perbankan)." data-testid="input-message" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 text-[13.5px] leading-relaxed text-ink">
                      <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[#0284C7]" data-testid="checkbox-consent" />
                      <span>Saya menyetujui pemrosesan data untuk keperluan konsultasi sesuai <Link to="/kebijakan-privasi" className="font-semibold text-accentb underline underline-offset-4" data-testid="consent-privacy-link">Kebijakan Privasi</Link>.</span>
                    </label>
                    {errors.consent && <span className="mt-1.5 block text-[12.5px] font-medium text-red-600" data-testid="error-consent">{errors.consent}</span>}
                  </div>
                  {status === "error" && (
                    <p className="rounded-xl bg-red-50 p-4 text-[13.5px] text-red-700 sm:col-span-2" data-testid="consult-error">
                      Maaf, formulir belum berhasil dikirim. Silakan coba lagi atau hubungi kami melalui <a href={waLink()} className="font-semibold underline" target="_blank" rel="noopener noreferrer">WhatsApp</a>.
                    </p>
                  )}
                  <button type="submit" disabled={status === "loading"} className="btn btn-slate !py-4 disabled:opacity-70 sm:col-span-2" data-testid="consult-submit">
                    {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Mengirim…</> : <>Kirim &amp; Mulai Konsultasi <ArrowUpRight size={16} /></>}
                  </button>
                </form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
